<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { getApiErrorMessage } from '@/utils/apiError'

import userService from '../../services/userService'

definePage({
  meta: {
    title: 'pages.user.resetPassword.title',
    requiresAuth: false,
  },
})

const email = ref('')
const token = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const isGuestClaim = computed(() => route.query.mode === 'claim')

const navigateToLogin = () => {
  router.push('/user/login')
}

onMounted(() => {
  // Extract token and email from route query parameters
  if (route.query.token) {
    token.value = route.query.token.toString()
  }
  if (route.query.email) {
    email.value = route.query.email.toString()
  }
})

const resetPassword = async () => {
  if (!email.value || !token.value || !newPassword.value || !confirmPassword.value) {
    errorMessage.value = t('pages.user.resetPassword.messages.requiredFields')
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = t('pages.user.resetPassword.messages.passwordMismatch')
    return
  }

  try {
    loading.value = true
    errorMessage.value = ''
    const success = await userService.resetPassword(
      email.value,
      token.value,
      newPassword.value,
      confirmPassword.value,
    )

    if (success) {
      successMessage.value = isGuestClaim.value
        ? t('pages.user.resetPassword.claim.success')
        : t('pages.user.resetPassword.messages.success')
      setTimeout(() => {
        navigateToLogin()
      }, 3000)
    } else {
      errorMessage.value = t('pages.user.resetPassword.messages.genericError')
    }
  } catch (error: unknown) {
    console.error('Reset password error:', error)
    errorMessage.value = getApiErrorMessage(
      error,
      t,
      t('pages.user.resetPassword.messages.genericError'),
    )
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div style="max-width: 400px" class="mycard px-4 py-2">
    <div class="mb-4 flex flex-row justify-between">
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-200">
        {{
          isGuestClaim
            ? t('pages.user.resetPassword.claim.title')
            : t('pages.user.resetPassword.title')
        }}
      </h1>
    </div>
    <p class="mb-4 text-muted">
      {{
        isGuestClaim
          ? t('pages.user.resetPassword.claim.intro')
          : t('pages.user.resetPassword.intro')
      }}
    </p>

    <AppAlert v-if="errorMessage" color="error" :closable="false" class="mb-4">
      {{ errorMessage }}
    </AppAlert>
    <AppAlert v-if="successMessage" color="success" :closable="false" class="mb-4">
      {{ successMessage }}
    </AppAlert>

    <form class="flex flex-col gap-3" @submit.prevent="resetPassword">
      <AppFloatField v-if="!email" variant="on">
        <UInput id="email" v-model="email" class="w-full" autocomplete="email" />
        <label for="email">{{ t('fields.user.email') }}</label>
      </AppFloatField>

      <AppFloatField v-if="!token" variant="on">
        <UInput id="token" v-model="token" class="w-full" />
        <label for="token">{{ t('pages.user.resetPassword.token') }}</label>
      </AppFloatField>

      <AppFloatField variant="on">
        <AppPassword
          id="newPassword"
          v-model="newPassword"
          class="w-full"
          autocomplete="new-password"
          toggle-mask
          feedback
        />
        <label for="newPassword">{{ t('pages.user.resetPassword.newPassword') }}</label>
      </AppFloatField>

      <AppFloatField variant="on">
        <AppPassword
          id="confirmPassword"
          v-model="confirmPassword"
          class="w-full"
          autocomplete="new-password"
          toggle-mask
        />
        <label for="confirmPassword">{{ t('pages.user.resetPassword.confirmPassword') }}</label>
      </AppFloatField>

      <div class="mt-2 flex flex-row gap-2">
        <UButton
          type="submit"
          :label="
            isGuestClaim
              ? t('pages.user.resetPassword.claim.submit')
              : t('pages.user.resetPassword.submit')
          "
          class="w-full"
          :loading="loading"
          :disabled="!email || !token || !newPassword || !confirmPassword"
        />
      </div>

      <div class="mt-3 flex flex-row justify-center">
        <a
          class="cursor-pointer font-semibold text-blue-700 dark:text-blue-400"
          @click="navigateToLogin"
        >
          {{ t('pages.user.resetPassword.backToLogin') }}
        </a>
      </div>
    </form>
  </div>
</template>
